import React, { useState, useEffect } from 'react';
import { Mail, Lock, ArrowLeft, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import { HelpButton } from '@/features/help/components/HelpButton';

/**
 * LoginPage component that handles user authentication
 */
export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const {
    login,
    isLoading: authIsLoading,
    isLoggingIn,
    error: authError,
    requestPasswordReset,
    clearAuthError,
  } = useAuthStore();
  const [emailVerificationMessage, setEmailVerificationMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Check if user was redirected after account deletion
  const accountDeleted = searchParams.get('accountDeleted') === 'true';

  /**
   * Log when the component mounts
   */
  useEffect(() => {
    logger.info('Auth', 'Login page loaded');
    setEmailVerificationMessage(null);
    if (!emailVerificationMessage) {
      clearAuthError();
    }
    setError(null);
    return () => {
      logger.debug('Auth', 'Login page unmounted');
    };
  }, [clearAuthError, emailVerificationMessage]);

  /**
   * Handles form submission for user login
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAuthError();
    setEmailVerificationMessage(null);

    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }

    logger.info('Auth', 'Login attempt initiated', { email });

    try {
      const user = await login(email, password);
      if (user) {
        logger.info('Auth', 'Login attempt successful, checking email verification status', {
          directUserEmailVerified: user.emailVerified,
          storeUserEmailVerified: useAuthStore.getState().firebaseUser?.emailVerified,
          userId: user.uid,
        });
        if (user.emailVerified) {
          toast.success('Login successful!');
          navigate('/dashboard');
        } else {
          logger.info('Auth', 'User email not verified, redirecting to /verify-email', {
            userId: user.uid,
          });
          navigate('/verify-email');
        }
      } else {
        logger.warn(
          'Auth',
          'Login attempt completed but no user object returned and no error thrown explicitly. AuthError from store will be shown.',
          { authError: useAuthStore.getState().error }
        );
      }
    } catch (error: any) {
      logger.error('Auth', 'Login failed, error caught in component', {
        error: error,
        authStoreError: useAuthStore.getState().error,
        fullError: error,
      });
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address to reset your password.');
      toast.error('Please enter your email address to reset your password.');
      return;
    }
    try {
      await requestPasswordReset(email);
      toast.success('Password reset email sent! Please check your inbox (and spam folder).');
      setError(null);
      setEmailVerificationMessage(null);
    } catch {
      toast.error(useAuthStore.getState().error || 'Failed to send password reset email.');
    }
  };

  return (
    <div className="min-h-screen bg-white font-inter flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 text-gray-600 hover:text-gray-900 transition-colors"
        aria-label="Go back"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl border border-blue-200 shadow-2xl shadow-blue-500/30 ring-4 ring-blue-300/20 relative before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-r before:from-blue-400/20 before:to-purple-400/20 before:blur-xl before:-z-10">
        {/* Account Deleted Confirmation Banner */}
        {accountDeleted && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="text-sm font-medium">Your account has been successfully deleted.</p>
            </div>
          </div>
        )}

        <div>
          <h2 className={`${accountDeleted ? 'mt-2' : 'mt-6'} text-center text-3xl font-bold text-gray-900`}>
            Sign in to your account
          </h2>
          {error && !authError && (
            <p className="mt-2 text-center text-sm text-red-600" data-testid="form-error-message">
              {error}
            </p>
          )}
          {authError && (
            <p className="mt-2 text-center text-sm text-red-600" data-testid="login-error-message">
              {authError}
            </p>
          )}
          {emailVerificationMessage && !authError && (
            <p
              className="mt-2 text-center text-sm text-yellow-600"
              data-testid="email-verification-message"
            >
              {emailVerificationMessage}
              {!useAuthStore.getState().firebaseUser?.emailVerified &&
                useAuthStore.getState().firebaseUser && (
                  <button
                    onClick={async () => {
                      const { resendVerificationEmail: resendEmail } = useAuthStore.getState();
                      const success = await resendEmail();
                      if (success && !useAuthStore.getState().error) {
                        toast.success('Verification email resent! Please check your inbox.');
                      } else {
                        toast.error(useAuthStore.getState().error || 'Failed to resend email.');
                      }
                    }}
                    disabled={authIsLoading}
                    className="ml-2 underline text-blue-500 hover:text-blue-600 text-xs"
                  >
                    Resend verification email
                  </button>
                )}
            </p>
          )}
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/signup" className="font-medium text-blue-500 hover:text-blue-600">
              create a new account
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div className="relative">
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                data-testid="login-email-input"
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                data-testid="login-password-input"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handlePasswordReset();
                }}
                className="font-medium text-blue-500 hover:text-blue-600"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoggingIn}
              data-testid="login-submit-button"
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white 
                ${isLoggingIn ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {isLoggingIn ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Help Button */}
      <HelpButton variant="fixed" />
    </div>
  );
};
